import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { CallDetailsContext } from '../types';
import { getRecentCallsUniqueKey } from '../utils';
import { CallDetailsScreen } from './call-details-screen';
import { CallQualityDrilldownScreenContainer } from './call-quality-drilldown-screen-old';
import { CallQualityScreenOld } from './call-quality-screen-old';
import { SCREENS } from './const';
import { ScreenBase, ScreenStateType } from './types';

const Screens: Record<keyof typeof SCREENS, ({ setScreen, data }: ScreenBase<any>) => ReactNode> = {
  [SCREENS.CALL_DETAILS]: CallDetailsScreen,
  [SCREENS.CALL_QUALITY]: CallQualityScreenOld,
  [SCREENS.CALL_QUALITY_DRILLDOWN]: CallQualityDrilldownScreenContainer,
};

export const CallDetailsPanel = () => {
  const elRef = useRef<HTMLDivElement>(null);
  const [screenState, setScreenState] = useState<ScreenStateType>({
    screen: SCREENS.CALL_DETAILS,
  });
  const Screen = screenState.screen ? Screens[screenState.screen] : Screens[SCREENS.CALL_DETAILS];

  const { context } = useSlidePanelShallowStore<CallDetailsContext>('setShow', 'context', 'show');
  const row = context?.rows.find((row) => row.id === context.currentRowId);
  const currentRowIndex = context?.rows.findIndex((row) => getRecentCallsUniqueKey(row) === context.currentRowId) ?? 0;

  const callDetailsUniqueKey = !!row ? getRecentCallsUniqueKey(row) : '';

  // This effect is used to scroll and highlight the selected row into view when the component mounts or when the row changes.
  useEffect(() => {
    if (row) {
      context?.toggleRowSelected?.(callDetailsUniqueKey, true, currentRowIndex);
      const rowElement = document.querySelectorAll(`#${context?.instanceId} [role=row]`)[currentRowIndex];
      rowElement?.scrollIntoView({ behavior: 'smooth' });
    }
    return () => {
      if (context && row) {
        context.toggleRowSelected?.(callDetailsUniqueKey, false, currentRowIndex);
      }
    };
  }, [row]);

  /**
   * Because this tray is disconnected from the main panels, the easiest and cleanest way to communicate is to send a custom event.
   * This effect binds a listener to the custom event 'reset-panel' that is dispatched from the main panel.
   */
  useEffect(() => {
    const element = elRef.current;
    const callback = (e: Event) => {
      if (e.target === element && screenState.screen !== SCREENS.CALL_DETAILS) {
        setScreenState({ screen: SCREENS.CALL_DETAILS });
      }
    };
    if (element) {
      element.addEventListener('reset-panel', callback);
    }

    return () => {
      if (element) {
        element.removeEventListener('reset-panel', callback);
      }
    };
  }, [screenState.screen]);

  if (!context || !row) {
    return null;
  }

  return (
    <div id='call-details-panel' style={{ height: '100%' }} ref={elRef}>
      <Screen setScreen={setScreenState} data={screenState.data} />
    </div>
  );
};
